import AssessmentOutlined from "@mui/icons-material/AssessmentOutlined";
import { Button, Divider, FormControl, Grid, Typography } from "@mui/material";
import dayjs from "dayjs";
import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import AsyncSearch from "../../components/asyncSearch/AsyncSearch";
import { BackdropCustom } from "../../components/backdrop/Backdrop";
import { ButtonGroup } from "../../components/buttonGroup/ButtonGroup";
import { ButtonTheme } from "../../components/buttonTheme/ButtonTheme";
import { Form } from "../../components/form/Form";
import { PageCard } from "../../components/pageCard/PageCard";
import { TitleContainer } from "../../components/titleContainer/TitleContainer";
import { useToastr } from "../../components/toast/Toast";
import { ICollector } from "../../interfaces/ICollector";
import { IExhibition } from "../../interfaces/IExhibition";
import { IOption } from "../../interfaces/IOption";
import { IPageProps } from "../../interfaces/IPageProps";
import api from "../../services/api";
import { formatCEP } from "../../utils/formatters";
import {
  ExhibitionCard,
  ExhibitionCardActions,
  ExhibitionCardContent,
} from "../exhibitionsJudges/ExhibitionsJudges.styles";

export const Collector: React.FC<IPageProps> = ({ pageTitle }) => {
  window.document.title = pageTitle;

  const timeout = useRef<any>(0);
  const location = useLocation();
  const navigate = useNavigate();
  const toastr = useToastr();

  const [collectorData, setCollectorData] = useState<ICollector | null>(null);

  const [collectors, setCollectors] = useState<ICollector[]>([]);
  const [collectorOptions, setCollectorOptions] = useState<IOption[]>([]);
  const [collectorOption, setCollectorOption] = useState<IOption | null>(null);

  const [openExhibitions, setOpenExhibitions] = useState<IExhibition[]>([]);

  const [loadingCollectors, setLoadingCollectors] = useState(false);
  const [loading, setLoading] = useState(false);

  const collectorToOption = (collector: ICollector): IOption => {
    return {
      value: `${collector.id}`,
      description: `${collector.name}`,
    };
  };

  const handleListOpenExhibitions = useCallback(async () => {
    setLoading(true);
    try {
      const response = await api.get(
        "/exhibitions/open?perPage=100&currentPage=1&orderBy=date&orderDirection=asc",
      );
      const exhibitionsData = response.data.data || [];
      setOpenExhibitions(exhibitionsData.slice(0, 100));
    } catch (error: any) {
      toastr.error(
        error.response?.data?.message || "Erro ao buscar exposições abertas",
      );
    } finally {
      setLoading(false);
    }
  }, [toastr]);

  const handleFetchCollectorById = useCallback(
    async (id: string) => {
      setLoading(true);
      try {
        const response = await api.get(`/collectors/${id}`);
        setCollectorData(response.data);
        setCollectorOption(collectorToOption(response.data));
        localStorage.setItem("@ORQUIDEAS:lastCollectorId", id);
        await handleListOpenExhibitions();
      } catch (error: any) {
        toastr.error(
          error.response?.data?.message ||
            "Erro ao buscar dados do colecionador",
        );
      } finally {
        setLoading(false);
      }
    },
    [toastr, handleListOpenExhibitions],
  );

  const handleSetCollectorData = useCallback(
    (option: IOption | null) => {
      const selectedCollector = collectors.find(c => c.id === option?.value);
      if (selectedCollector) {
        setCollectorOption(option);
        setCollectorData(selectedCollector);
        localStorage.setItem(
          "@ORQUIDEAS:lastCollectorId",
          selectedCollector.id,
        );
        handleListOpenExhibitions();
      }
    },
    [collectors, handleListOpenExhibitions],
  );

  const handleFindCollector = useCallback(async () => {
    const idFromUrl =
      location.pathname.replace("/collector", "").replace("/", "") || null;

    if (idFromUrl) {
      await handleFetchCollectorById(idFromUrl);
    } else {
      const lastId = localStorage.getItem("@ORQUIDEAS:lastCollectorId");
      if (lastId) {
        await handleFetchCollectorById(lastId);
      }
    }
  }, [location.pathname, handleFetchCollectorById]);

  const handleListCollectors = useCallback(
    async (filter = "") => {
      clearInterval(timeout.current);
      timeout.current = setTimeout(async () => {
        setLoadingCollectors(true);

        await api
          .get(
            `/collectors/dynamically?perPage=100&currentPage=1&orderBy=name&orderDirection=asc&filter=${filter}`,
          )
          .then(result => {
            if (result) {
              setCollectors(result.data.data);
              const options: IOption[] =
                result.data.data.map(collectorToOption);

              setCollectorOptions(options);
            } else {
              setCollectorOptions([]);
            }
          })
          .catch(error => {
            setCollectorOptions([]);
            toastr.error(error.message);
          })
          .finally(() => {
            setLoadingCollectors(false);
          });
      }, timeout.current);
    },
    [toastr],
  );

  const handleToCollectorPlants = (exhibitionId: string) => {
    if (collectorData?.id && exhibitionId) {
      navigate(`/collector-plants/${exhibitionId}/${collectorData.id}`);
    }
  };

  useEffect(() => {
    handleFindCollector();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <TitleContainer>
        <h1>Colecionador</h1>
      </TitleContainer>
      <PageCard>
        <Form>
          <Grid container spacing={2} alignItems="center" sx={{ mb: 4 }}>
            <Grid item xs={12} sm={6}>
              <FormControl size="small" sx={{ width: "100%" }} required>
                <AsyncSearch
                  options={collectorOptions}
                  setOptions={setCollectorOptions}
                  option={collectorOption}
                  setOption={handleSetCollectorData}
                  asyncSearch={handleListCollectors}
                  loading={loadingCollectors}
                  label="Colecionador"
                />
              </FormControl>
            </Grid>
            {!collectorData && (
              <Grid item xs={12} sm={6}>
                <ButtonTheme
                  variant="contained"
                  onClick={() => navigate("/sign-up")}
                >
                  Novo Colecionador
                </ButtonTheme>
              </Grid>
            )}
          </Grid>
        </Form>

        <Divider sx={{ mb: 1 }} />

        {collectorData && (
          <>
            <Grid container spacing={1.5}>
              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ fontWeight: 600, mb: 1 }}
                >
                  Dados do Colecionador
                </Typography>
                <Divider sx={{ mb: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    display: "block",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  Nome
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {collectorData.name}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    display: "block",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  Usuário
                </Typography>
                <Typography variant="body2">
                  {collectorData.username}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    display: "block",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  E-mail
                </Typography>
                <Typography variant="body2">
                  {collectorData.email || "-"}
                </Typography>
              </Grid>

              {collectorData.association && (
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{
                      display: "block",
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  >
                    Associação
                  </Typography>
                  <Typography variant="body2">
                    {collectorData.association}
                  </Typography>
                </Grid>
              )}

              <Grid item xs={12}>
                <Typography
                  variant="h6"
                  color="primary"
                  sx={{ mt: 1, fontWeight: 600 }}
                >
                  Localização
                </Typography>
                <Divider sx={{ mb: 1 }} />
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    display: "block",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  CEP
                </Typography>
                <Typography variant="body2">
                  {formatCEP(collectorData.zipCode)}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    display: "block",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  Cidade/Estado
                </Typography>
                <Typography variant="body2">
                  {collectorData.city || "-"} - {collectorData.state?.id || "-"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    display: "block",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  País
                </Typography>
                <Typography variant="body2">
                  {collectorData.country || "-"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    display: "block",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  Bairro
                </Typography>
                <Typography variant="body2">
                  {collectorData.neighborhood || "-"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Typography
                  variant="caption"
                  color="textSecondary"
                  sx={{
                    display: "block",
                    textTransform: "uppercase",
                    fontWeight: 700,
                  }}
                >
                  Endereço
                </Typography>
                <Typography variant="body2">
                  {collectorData.address || "-"},{" "}
                  {collectorData.addressNumber || "S/N"}
                </Typography>
              </Grid>

              {collectorData.addressComplement && (
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{
                      display: "block",
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  >
                    Complemento
                  </Typography>
                  <Typography variant="body2">
                    {collectorData.addressComplement}
                  </Typography>
                </Grid>
              )}

              {collectorData.addressReference && (
                <Grid item xs={12} sm={6}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    sx={{
                      display: "block",
                      textTransform: "uppercase",
                      fontWeight: 700,
                    }}
                  >
                    Referência
                  </Typography>
                  <Typography variant="body2">
                    {collectorData.addressReference}
                  </Typography>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 3 }} />

            <Typography
              variant="h6"
              color="primary"
              sx={{ fontWeight: 600, mb: 2 }}
            >
              Exposições com Inscrições Abertas
            </Typography>

            <Grid container spacing={2}>
              {openExhibitions.length > 0 ? (
                openExhibitions.map(exhibition => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={exhibition.id}>
                    <ExhibitionCard>
                      <ExhibitionCardContent>
                        <h1>{exhibition.name}</h1>
                        <p>
                          {exhibition.date
                            ? dayjs(exhibition.date).format("DD/MM/YYYY")
                            : ""}
                        </p>
                        <p>{exhibition.details || "Sem observação"}</p>
                        <span>
                          {exhibition.city} - {exhibition.stateId}
                        </span>
                      </ExhibitionCardContent>
                      <ExhibitionCardActions>
                        <Button
                          variant="outlined"
                          size="small"
                          startIcon={<AssessmentOutlined />}
                          onClick={() =>
                            handleToCollectorPlants(exhibition.id || "")
                          }
                          sx={{ fontSize: "0.75rem" }}
                        >
                          Minhas Plantas
                        </Button>
                      </ExhibitionCardActions>
                    </ExhibitionCard>
                  </Grid>
                ))
              ) : (
                <Grid item xs={12}>
                  <Typography variant="body2" color="textSecondary">
                    Nenhuma exposição aberta encontrada.
                  </Typography>
                </Grid>
              )}
            </Grid>

            <ButtonGroup>
              <ButtonTheme
                onClick={() => navigate("/")}
                variant="contained"
                color="inherit"
              >
                Voltar
              </ButtonTheme>
            </ButtonGroup>
          </>
        )}

        {loading && <BackdropCustom />}
      </PageCard>
    </>
  );
};
